/**
 * Reserve onboarding tooltips. Replace placeholder copy with
 * product-specific descriptions when ready.
 */
export const reserveSectionTooltip = {
  header:
    "Reserve onboarding. Detailed descriptions for this section will be added later.",
};

/** Outlet setup — main section (aligned with Retail outlet setup). */
export const reserveOutletSetupTooltip = {
  header: "List each Reserve outlet below.",
  outletName: "The name of the shop or outlet",
  terminalId: "Enter 1 here",
  profitCenterId:
    "Leave this field blank; this will be autopopulated based on your outlet setup",
  autoPrintReceipts:
    "If you'd like to automatically print receipts in this outlet, enter Yes. If not, enter No.",
  active: "Is this outlet active? Yes or No",
};

export const reserveLocationTooltip = {
  header:
    "Configure locations for Reserve. Use the list below to reorder or edit entries.",
  area: "Description for Area will be added later.",
  locationType: "Description for Location Type will be added later.",
};

export const reserveTablesTooltip = {
  header:
    "Configure tables for Reserve Dining. Use the list below to reorder or edit entries.",
  seatingArea: "Description for Seating Area will be added later.",
  seatingType: "Description for Seating Type will be added later.",
};

/** Reservation guest notes — main section (same pattern as other Reserve list pages). */
export const reserveReservationGuestNotesTooltip = {
  header:
    "Configure reservation guest notes for Reserve. Use the list below to reorder or edit entries.",
  category:
    "Optional category label used to group or filter this guest note configuration in Reserve.",
  subcategory:
    "Optional subcategory for finer grouping under the selected category.",
  reservationNotes:
    "Text shown or stored with the reservation context (for example, internal reservation notes).",
  guestNotes:
    "Text associated with the guest-facing or guest profile notes for this configuration.",
};

/** Shown next to “{Section} available” list headings on Reserve list screens. */
export const reserveListAvailableHeadingTooltip =
  "Saved rows for this section. Drag to reorder, or use Edit / Delete on each row.";

export const reservePropertyInformationTooltipHeader =
  "Enter contact and location details for this Reserve property. Values save with your draft.";

/** Default row field tooltips for Reserve list screens (Code / Name / Active). */
export const reserveRowFieldTooltip = {
  header:
    "Configure entries for this section. Use the list below to reorder or edit rows.",
  code: "Primary value for this row. Details will be added later.",
  name: "Secondary value for this row. Details will be added later.",
  active: "When enabled, this row is active.",
};

export const reserveRolesTooltip = {
  header:
    "Define roles for Reserve. Use the list below to reorder or edit entries.",
  roleCode:
    "Short code used within the application. Details will be added later.",
  roleName: "Display name for the role. Details will be added later.",
  active: "When enabled, this role is active.",
};

export const reserveUserDetailsTooltip = {
  header:
    "Configure user account details for Reserve. Save changes with your draft.",
  name: "Display name for this user account.",
  firstName: "User's given name.",
  lastName: "User's family name.",
  countryCode: "Dialing prefix for the user's mobile number (e.g. +1).",
  mobile: "Mobile phone number (digits; country code is separate).",
  emailAddress: "Work or login email for this user.",
  employeeCode: "Unique employee identifier within the property.",
  username: "Unique sign-in username for this user.",
  pin: "Numeric or alphanumeric PIN used for kiosk or secondary auth.",
  retailRole: "When enabled, this user has retail-related permissions.",
  password:
    "Set or change the sign-in password. When editing, leave blank to keep the current password.",
  role: "Assign a role defined on the Roles screen. Role codes must match an active role.",
  outletAccess:
    "Outlets this user can access. Select one or more from Outlet Setup.",
};

export const reserveUserPermissionsTooltipHeader =
  "Configure permissions by role for Reserve. Expand sections to edit actions.";
export const reserveUserPermissionsTooltipRoleSelect =
  "Choose which role these permissions apply to.";
export const reserveUserPermissionsTooltipPermissionArea =
  "Expand each section (same pattern as Room Type Maintenance). Use the dropdown for quick clear or enable-all, then fine-tune with toggles.";
export const reserveUserPermissionsTooltipCategoryBulkSelect =
  "Clear all turns off every action in this category. Enable all turns them all on. When the mix is partial, the dropdown shows Mixed.";

export const reserveServiceChargeTooltip = {
  header:
    "Configure service charges for Reserve. Use the list below to reorder or edit entries.",
  taxType:
    "Category or program this service charge applies to (for example, Personal Training).",
  value: "Charge amount or rate for this tax type. Leave blank if not set yet.",
  calculationType:
    "Whether the value is a flat amount or a percentage of the base.",
  startDate: "Date this service charge becomes effective (optional).",
  endDate: "Date this service charge stops applying (optional).",
};

export const reserveTaxesTooltip = {
  header:
    "Configure taxes for Reserve. Use the list below to reorder or edit entries.",
  taxType: "Name of the tax (for example, Sales Tax).",
  value: "Tax rate or amount (for example, 8% or a flat value).",
  calculationType:
    "Whether the value is a flat amount or a percentage of the taxable base.",
  startDate: "Date this tax becomes effective (optional).",
  endDate: "Date this tax stops applying (optional).",
};

export const reserveInstructorsTooltip = {
  header:
    "Configure instructors for Reserve. Use the list below to reorder or edit entries.",
  nameInstructorLevel:
    "Derived list label: first and last name, plus Trainer when expertise is Trainer.",
  firstName: "Instructor first name.",
  lastName: "Instructor last name.",
  employeeCode: "Unique employee identifier for this instructor.",
  expertiseLevel:
    "None for standard instructors, Trainer when they act as a trainer.",
  countryCode: "Dialing prefix for mobile (for example +1).",
  mobile: "Contact number; when provided it must be a valid 10-digit number.",
  emailAddress: "Optional work email for this instructor.",
};

export const reserveMiscItemsTooltipHeader =
  "Miscellaneous reservation settings. Values save with your draft automatically.";
export const reserveMiscItemsTooltipGuestWaiverFormRequired =
  "Whether guests must complete a waiver before participating.";
export const reserveMiscItemsTooltipConfirmationEmail =
  "When to send booking confirmation emails.";
export const reserveMiscItemsTooltipUpdateEmail =
  "When to send emails after reservation changes.";
export const reserveMiscItemsTooltipCancelEmail =
  "When to send cancellation emails.";
export const reserveMiscItemsTooltipAllowBookEditPast =
  "Whether staff may create or change reservations in the past.";
export const reserveMiscItemsTooltipAllowEditCheckedIn =
  "Whether checked-in reservations can still be edited.";
export const reserveMiscItemsTooltipPastDaysToViewEdit =
  "How many calendar days back reservations can be viewed or edited.";
export const reserveMiscItemsTooltipDefaultCommunicationPreference =
  "Default channel for guest communication (Email, Phone, or both).";

export const reserveEquipmentsAddonsTooltipHeader =
  "Configure equipment and add-ons for Reserve. Use the list below to reorder or edit entries.";
export const reserveEquipmentsAddonsTooltipAddOnName =
  "Display name for the add-on (for example, Dry Bags).";
export const reserveEquipmentsAddonsTooltipAddOnType =
  "How the add-on is sold (for example, Per Reservation).";
export const reserveEquipmentsAddonsTooltipAddOnDescription =
  "Optional longer description for staff or guests.";
export const reserveEquipmentsAddonsTooltipAddOnCategory =
  "Primary merchandising or activity category.";
export const reserveEquipmentsAddonsTooltipAddOnSubCategory =
  "More specific grouping under the category.";
export const reserveEquipmentsAddonsTooltipQtyInStock =
  "Units available to assign or sell.";
export const reserveEquipmentsAddonsTooltipPricingType =
  "How price is calculated: By duration or By session.";
export const reserveEquipmentsAddonsTooltipPrice =
  "Base price text (for example, $10).";
export const reserveEquipmentsAddonsTooltipAdditionalRatesPerMins =
  "Extra rate per minute if duration pricing applies.";

export const reserveActivitiesTooltipHeader =
  "Configure activities for Reserve. Use the list below to reorder or edit entries.";
export const reserveActivitiesTooltipActivityCode =
  "Unique code for this activity (for example, AA123).";
export const reserveActivitiesTooltipActivityName =
  "Guest-facing activity title.";
export const reserveActivitiesTooltipActivityType =
  "Group lesson, private lesson, or open booking.";
export const reserveActivitiesTooltipClassType =
  "Whether scheduling or pricing is by session or by class.";
export const reserveActivitiesTooltipActivityDescription =
  "Full description shown where the activity is marketed or explained.";
export const reserveActivitiesTooltipActivityCategory =
  "Top-level category (for example, Fitness).";
export const reserveActivitiesTooltipActivitySubCategory =
  "More specific grouping under the category.";
export const reserveActivitiesTooltipMinGroupSize =
  "Smallest group size allowed to book.";
export const reserveActivitiesTooltipMaxGroupSize =
  "Largest group size allowed to book.";
export const reserveActivitiesTooltipMinActivityStrength =
  "Minimum capacity or intensity if applicable (or N/A).";
export const reserveActivitiesTooltipMaxActivityStrength =
  "Maximum capacity or intensity if applicable (or N/A).";
export const reserveActivitiesTooltipAllowOverbookReservation =
  "Whether bookings may exceed normal capacity.";
export const reserveActivitiesTooltipAllowStandbyReservation =
  "Whether guests can join a standby list.";
export const reserveActivitiesTooltipAutoConfirmStandbyAfterCancellation =
  "Whether standby guests are auto-confirmed when a spot opens.";
export const reserveActivitiesTooltipMaxStandbyStrength =
  "Maximum number of standby slots.";
export const reserveActivitiesTooltipGuestType =
  "Which guest types may book this activity.";
export const reserveActivitiesTooltipGuestTypePricing =
  "Pricing rules by guest type.";
export const reserveActivitiesTooltipMembershipType =
  "Membership categories that affect pricing.";
export const reserveActivitiesTooltipMembershipPricing =
  "Price for applicable membership types.";
export const reserveActivitiesTooltipStartDate =
  "First date this activity configuration is active.";
export const reserveActivitiesTooltipEndDate =
  "Last date this activity configuration is active.";
export const reserveActivitiesTooltipDaysOfTheWeek =
  "Which weekdays apply (for example, M, T, W, TH, F).";
export const reserveActivitiesTooltipStartEndTime =
  "Daily operating window (for example, 1:00 PM - 2:00 PM).";
export const reserveActivitiesTooltipStartTime =
  "Daily start time for this activity.";
export const reserveActivitiesTooltipEndTime =
  "Daily end time for this activity.";
export const reserveActivitiesTooltipNumberOfLocations =
  "How many locations or slots apply.";
export const reserveActivitiesTooltipLocationName =
  "Where the activity is held.";
export const reserveActivitiesTooltipStaffGuide =
  "Default staff or guide name.";
export const reserveActivitiesTooltipRequirePrepayment =
  "Whether payment is required before the activity.";
export const reserveActivitiesTooltipPaymentType =
  "How payment is collected (for example, Charge on Booking).";
export const reserveActivitiesTooltipBasePrice =
  "Base price text (for example, 25 per person).";
export const reserveActivitiesTooltipCancellationFeeRequired =
  "Describe or enter when a cancellation fee applies (free text, for example amount or policy reference).";
export const reserveActivitiesTooltipCancellationFeeType =
  "How the fee is calculated (for example, Percentage).";
export const reserveActivitiesTooltipCancellationDeadline =
  "When cancellation rules take effect.";
export const reserveActivitiesTooltipCancellationPolicyDescription =
  "Full cancellation policy text for guests.";
export const reserveActivitiesTooltipAllowAdditionalGuestDetails =
  "Whether to collect extra guest information.";
export const reserveActivitiesTooltipCollectAdditionalEmployeeDetails =
  "Whether to collect extra employee-related details at booking.";
export const reserveActivitiesTooltipActivityImage =
  "URL or path to a wide banner image (recommended 1920×1080).";
export const reserveActivitiesTooltipReservationParameter =
  "Age, height, weight, or other restrictions.";
export const reserveActivitiesTooltipAddonsName =
  "Add-ons and whether each is mandatory or optional.";
export const reserveActivitiesTooltipAvailableForWebReservation =
  "Whether this activity is offered for web booking.";
export const reserveActivitiesTooltipCheckInCutoffMinutes =
  "Minutes before start when check-in closes.";

export const reserveOperationHoursTooltip = {
  header:
    "Configure operating hours for Reserve. Use the list below to reorder or edit entries.",
  day: "Which day of the week this row applies to.",
  openHours:
    "Public or facility open window for that day (start and end time).",
  staffShifts:
    "One or more staff coverage windows. Add rows for each segment; use Shift Name only for named blocks (for example, Late Night) without specific times.",
};

export const reserveRatePlanTooltip = {
  header:
    "Configure rate plans by activity for Reserve. Use the list below to reorder or edit entries.",
  activityName:
    "Special meal this rate plan applies to (for example, Vegan Lunch).",
  basePrice: "Default base price for the activity (for example, 50).",
  guestTypePricing:
    "Guest-type pricing lines (for example, 1.Adult - $100). Add one line per guest type.",
  hourlyPrice:
    "Hourly rate when applicable (for example, $30 for 1hr). Leave blank if not used.",
  dailyWeekly:
    "Daily or weekly rate details when applicable. Leave blank if not used.",
};

export const reserveReservationSettingsTooltip = {
  header:
    "Configure dining reservation settings for Reserve. Use the list below to reorder or edit entries.",
  acceptsOnlineReservations:
    "Whether guests can book reservations online (Yes or No).",
  maxPartySize: "Maximum number of guests per reservation (for example, 6).",
  daysInAdvance: "How far in advance guests may book (for example, 365 days).",
  reservationCutOffMinutes:
    "Minutes before service when online booking closes (for example, 15).",
  closeSameDayReservationsTime:
    "Cutoff for same-day online reservations (for example, No Restrictions).",
  minimumTimeToCancelReservation:
    "Minimum notice required to cancel (for example, 12 hours).",
  automaticOrManualSlotting:
    "Automatic assigns tables by the system; Manual requires staff assignment.",
  reservationHeldAfterResTime:
    "How long a table is held after reservation time (for example, 15 minutes).",
  autoSuggestTables:
    "Whether the system suggests table assignments (Yes or No).",
  noShowFeesPolicy:
    "No-show fee amount, per cover or per reservation, schedule, and applicable party size.",
};

export const reservePropertyQuestionsTooltip = {
  header:
    "Configure reservation property questions for Reserve. Use the list below to manage entries.",
  acceptsWebReservations: "Do you accept online reservations?",
  closeSameDayWebReservationAt:
    "Should be set if the outlet does not accept online reservations mid service. For example if the tour service starts at 5:00 PM the outlet may want to set this to 4:30 PM to prevent bookings from coming in after 4:30 PM.",
  requiredPartyField:
    'Select "phone" or "email". Field required when booking via Host Application. All reservations booked online require both a phone number and email address.',
  showCheckedInReservationsInTimelineView:
    "Allows checked-in reservations to remain in the Timeline view.",
};
